import { MutableRefObject, useState } from 'react'
import { GridApiPro } from '@microservices/wiskey-react-table-component'

import { useExecuteCommandMutation } from '@redux/api'

import { executeCode, getCommandRequestData, getPkField, getValuesFromVariables } from '@helpers'
import { ACTION_CODE, EVENT_CODE } from '@constants'
import { ActionRow, EntityType, GETAllVariables, ObjectDataRecord } from '@types'

import { EVENT_NAME, LocalEventBus, publish } from '../../../events'

type TUseDragAndDropForRow = {
  hasDragStartEvent: boolean | undefined
  hasDropEvent: boolean | undefined
  apiRef: MutableRefObject<GridApiPro>
  selectedRows: ObjectDataRecord[]
  actions: ActionRow[] | undefined
  outerVariables: GETAllVariables | undefined
  entity: EntityType | null
  outerEventBus: LocalEventBus | undefined
  draggableRowImageRef: React.RefObject<HTMLDivElement | null>
}

export const useDragAndDropForRow = ({
  hasDragStartEvent,
  hasDropEvent,
  apiRef,
  selectedRows,
  actions,
  outerVariables,
  entity,
  outerEventBus,
  draggableRowImageRef,
}: TUseDragAndDropForRow) => {
  const [countSelectedRowWithoutDraggableRow, setCountSelectedRowWithoutDraggableRow] = useState(0)
  const [displayDraggableRowImageText, setDisplayDraggableRowImageText] = useState('')

  const [executeCommand] = useExecuteCommandMutation()

  const getRowData = (element: HTMLElement) => {
    const rowId = element.getAttribute('data-id')

    if (!rowId) return null

    return apiRef.current.getRow(rowId)
  }

  const handleOnDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasDragStartEvent) return event.preventDefault()

    const currentDraggableElement = event.target as HTMLElement

    const rowData = getRowData(currentDraggableElement)

    if (!rowData) return event.preventDefault()

    setCountSelectedRowWithoutDraggableRow(
      selectedRows.filter(row => {
        if (row._id) {
          return row._id !== rowData._id
        }

        return row.internalId !== rowData.internalId
      }).length
    )

    const actionsWithOnDragStart =
      (actions &&
        actions.filter(action => {
          return action.eventCode === EVENT_CODE.ON_DRAG_START
        })) ||
      []

    const prepareOuterVariables = outerVariables
      ? outerVariables?.reduce((acc: Record<string | number, any>, variable) => {
          acc[variable.internalId] = variable

          return acc
        }, {})
      : {}

    const srcObj = {
      outerVariables: prepareOuterVariables,
    }

    const primaryKeyCode = getPkField(entity?.columns)?.code

    actionsWithOnDragStart.forEach(action => {
      setDisplayDraggableRowImageText(() => {
        return (
          (action?.draggableElementFieldCode && rowData[action?.draggableElementFieldCode]) ||
          (primaryKeyCode && rowData[primaryKeyCode]) ||
          'dragging element'
        )
      })

      if (action.actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE) {
        const targetVariable = action.jsValue
          ? executeCode(action.jsValue, { srcObj })
          : action.variable

        if (typeof targetVariable !== 'object' && targetVariable !== null) return

        const hasRowDataInSelectedArray = selectedRows.some(row => {
          return row._id === rowData._id
        })

        const newValueVariable = hasRowDataInSelectedArray
          ? selectedRows
          : [rowData, ...selectedRows]

        const newVariable = {
          ...targetVariable,
          defaultValue: newValueVariable,
        }

        if (outerEventBus) {
          outerEventBus.publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariable)

          return
        }

        publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariable)
      }
    })

    if (draggableRowImageRef.current === null) return

    event.dataTransfer.setDragImage(
      draggableRowImageRef.current,
      draggableRowImageRef.current.offsetWidth / 2,
      draggableRowImageRef.current.offsetHeight / 2
    )

    event.dataTransfer.effectAllowed = 'move'
  }

  const handleOnDragEnd = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const handleOnDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()

    if (!hasDropEvent) return

    const rowElement = event.currentTarget as HTMLElement
    rowElement.classList.add('row-drag-over')

    const cells = rowElement.querySelectorAll<HTMLElement>('.MuiDataGrid-cell')

    cells.forEach(cell => {
      cell.style.transition = 'none'
    })
  }

  const handleOnDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasDropEvent) return

    const rowElement = event.currentTarget as HTMLElement

    if (rowElement.contains(event.relatedTarget as Node)) {
      return
    }

    rowElement.classList.remove('row-drag-over')
  }

  const handleOnDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()

    const currentOnDropElement = event.currentTarget as HTMLElement

    currentOnDropElement.classList.remove('row-drag-over')

    const rowData = getRowData(currentOnDropElement)

    if (!rowData) return event.preventDefault()

    const actionsOnDrop =
      actions?.filter(action => {
        return action.eventCode === EVENT_CODE.ON_DROP
      }) || []

    for (const action of actionsOnDrop) {
      const { command, actionCode, jsValue } = action

      const srcObj = {
        rowData,
        outerVariables: getValuesFromVariables(outerVariables),
      }

      if (command) {
        if (actionCode === ACTION_CODE.EXECUTE_COMMAND) {
          try {
            const body = jsValue
              ? new Function('srcObj', jsValue)(srcObj)
              : getCommandRequestData(command.body ?? [], srcObj, undefined, undefined)

            const params = getCommandRequestData(
              command.parameters ?? [],
              srcObj,
              undefined,
              undefined
            )

            await executeCommand({
              method: command.httpMethod,
              objectCode: command.objectCode,
              commandCode: command.name,
              isCommandResponse: command.isCommandResponse,
              params,
              body,
            })
          } catch (err) {
            console.log(err)
          }
        }
      }
    }
  }

  return {
    handlers: {
      handleOnDragStart,
      handleOnDragEnd,
      handleOnDragOver,
      handleOnDragLeave,
      handleOnDrop,
    },
    state: {
      countSelectedRowWithoutDraggableRow,
      displayDraggableRowImageText,
    },
  }
}
